import { Dayjs } from 'dayjs'
import { useMemo } from 'react'
import { useGetFormOrdersQuery } from '../../../../store/api/form-orders-api'
import { useGetTasksQuery } from '../../../../store/api/tasks-api'
import { useUser } from '../../../../hooks/use-user'
import { EndpointQueryParameters } from '../../../../types/strapi/strapi'
import {
    buildDesignerMonthlySummary,
    getFormOrderIdsFromTasks,
} from '../helpers/calc-designer-monthly-stats'
import useDesignerOvertimeHours from './use-designer-overtime-hours'

const DESIGNER_BONUS_TASK_TYPES = [
    'Заказ форм',
    'Перезаказ форм',
    'Цифровой спуск',
    'Отрисовка штампа',
] as const

function buildTasksQueryParams(month: Dayjs): EndpointQueryParameters {
    const monthStart = month.startOf('month').toISOString()
    const monthEnd = month.endOf('month').toISOString()

    return {
        filters: {
            taskStatus: { $eq: 'Завершена' },
            updatedAt: { $gte: monthStart, $lte: monthEnd },
            type: {
                $in: [...DESIGNER_BONUS_TASK_TYPES],
            },
        },
        populate: {
            shipment: {
                populate: {
                    client: {
                        fields: ['name'],
                    },
                    stamp: {
                        populate: {
                            roller: true,
                        },
                    },
                },
            },
        },
        sort: ['updatedAt:desc'],
        pagination: { page: 1, pageSize: 500 },
    }
}

function buildFormOrdersQueryParams(formOrderIds: string[]): EndpointQueryParameters {
    return {
        filters: {
            documentId: { $in: formOrderIds },
        },
        populate: ['forms'],
        pagination: { page: 1, pageSize: 500 },
    }
}

export default function useDesignerCompletedTasks(month: Dayjs) {
    const user = useUser()

    const tasksQueryParams = useMemo(
        () => (user?.documentId ? buildTasksQueryParams(month) : undefined),
        [user?.documentId, month]
    )

    const {
        data: tasksData,
        isLoading: isTasksLoading,
        isFetching: isTasksFetching,
    } = useGetTasksQuery(tasksQueryParams, {
        skip: !user?.documentId,
    })

    const tasks = useMemo(() => tasksData?.data ?? [], [tasksData?.data])

    const formOrderIds = useMemo(() => getFormOrderIdsFromTasks(tasks), [tasks])

    const formOrdersQueryParams = useMemo(
        () => (formOrderIds.length ? buildFormOrdersQueryParams(formOrderIds) : undefined),
        [formOrderIds]
    )

    const {
        data: formOrdersData,
        isLoading: isFormOrdersLoading,
        isFetching: isFormOrdersFetching,
    } = useGetFormOrdersQuery(formOrdersQueryParams, {
        skip: !formOrderIds.length,
    })

    const formsCountByOrderId = useMemo(() => {
        const map = new Map<string, number>()

        for (const order of formOrdersData?.data ?? []) {
            map.set(order.documentId, order.forms?.length ?? 0)
        }

        return map
    }, [formOrdersData?.data])

    const { overtimeHours, isLoading: isOvertimeLoading } = useDesignerOvertimeHours(month)

    const { rows, stats } = useMemo(
        () => buildDesignerMonthlySummary(tasks, formsCountByOrderId, overtimeHours),
        [tasks, formsCountByOrderId, overtimeHours]
    )

    return {
        rows,
        isLoading:
            isTasksLoading ||
            isTasksFetching ||
            isOvertimeLoading ||
            (formOrderIds.length > 0 && (isFormOrdersLoading || isFormOrdersFetching)),
        stats,
    }
}
